import "@/App.css";
import { installDatePickerOnClick } from "@/lib/datePickerOnClick";
import React from "react";
import { BrowserRouter, Navigate, Route, Routes, Outlet } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { POSProvider, usePOS } from "@/context/POSContext";
import AppShell from "@/components/AppShell";
import { ErrorBoundary } from "@/components/ui/StateBoundaries";
import LoginPage from "@/pages/LoginPage";
import OperationalBillingPage from "@/pages/OperationalBillingPage";
import LicensePage from "@/pages/LicensePage";
import LicenseGate from "@/components/LicenseGate";
import CaptainLayout from "@/components/CaptainLayout";
import CaptainTables from "@/pages/captain/CaptainTables";
import CaptainCustomer from "@/pages/captain/CaptainCustomer";
import CaptainMenu from "@/pages/captain/CaptainMenu";
import CaptainSummary from "@/pages/captain/CaptainSummary";
import CaptainBill from "@/pages/captain/CaptainBill";
import CaptainKots from "@/pages/captain/CaptainKots";
import CaptainPendingBills from "@/pages/captain/CaptainPendingBills";
import BillingTables from "@/pages/billing/BillingTables";
import BillingTerminal from "@/pages/billing/BillingTerminal";
import KDSPage from "@/pages/KDSPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import InventoryPage from "@/pages/InventoryPage";
import PurchasePage from "@/pages/PurchasePage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import RegisterPage from "@/pages/RegisterPage";
import CRMKhataPage from "@/pages/CRMKhataPage";
import EnterpriseReportsPage from "@/pages/EnterpriseReportsPage";
import DashboardPage from "@/pages/DashboardPage";
import PhonePos from "@/components/PhonePos";
import StaffPage from "@/pages/StaffPage";
import StaffAttendancePage from "@/pages/StaffAttendancePage";
import AdminHubPage from "@/pages/AdminHubPage";
import { screenAllowed } from "@/lib/access";
import LoggingPage from "@/pages/LoggingPage";
import RouteLogger from "@/components/RouteLogger";
import RemoteBanner from "@/components/RemoteBanner";
import InsecureBanner from "@/components/InsecureBanner";

// Backend user_role enum is admin|captain|cashier. Vibez shipped this map naming
// "receptionist" and "kitchen", neither of which exists, so every cashier was locked
// out of every page. receptionist -> cashier; kitchen dropped, since the KDS terminal
// never logs in (backend/app/api/kds.py is deliberately unauthenticated).
const access = {
  billing:["admin","cashier"], floor:["admin","cashier","captain"], orders:["admin","cashier"],
  inventory:["admin"], purchase:["admin"], approvals:["admin"], register:["admin","cashier"], attendance:["admin"], crm:["admin","cashier"], reports:["admin"],
  staff:["admin"], captain:["admin","captain"], kds:["admin","cashier","captain"], admin:["admin"],
  logging:["admin","cashier","captain"], dashboard:["admin"]
};
// The screens follow the person's own switches — devices (2026-10-18), then KDS,
// inventory, purchase, CRM and each report (2026-10-20). An old cached session
// falls back to the role list; see lib/access.js.
export function Protected({ page, children, Shell = AppShell }) { 
  const { user, permissions } = usePOS(); 
  // Remember where the person was going (a Telegram link's ?date= included).
  if (!user) return <Navigate to="/login" replace state={{ from: `${window.location.pathname}${window.location.search}` }}/>; 
  const allowed = screenAllowed(page, user, permissions, access[page]);
  if (!allowed) {
    // Landing on the very page refused (no device switch at all) would loop.
    if (user.landing && !user.landing.startsWith(`/${page}`)) return <Navigate to={user.landing} replace/>;
    return (
      <div className="h-screen grid place-items-center p-6 text-center" data-testid="no-screen-assigned">
        <div>
          <h1 className="text-title font-bold">No screen is enabled for {user.name || "this account"}</h1>
          <p className="text-meta text-muted-foreground mt-2">This screen is not enabled for you. An admin can allow it for you in Staff.</p>
        </div>
      </div>
    );
  }
  return <Shell><ErrorBoundary>{children}</ErrorBoundary></Shell>; 
}
// A KDS hangs full-screen on a kitchen wall. AppShell gives it the operator
// sidebar it can never use and a header that repeats the outlet name the KDS
// already prints, so the page showed "Hotel The Vibez Inn" twice and spent a
// sixth of a kitchen screen on admin navigation. Bare shell, ErrorBoundary
// still applied by Protected.
// h-screen, not a bare fragment: KDSPage's root is `flex flex-col h-full`, so it
// needs a height-constrained parent or it collapses to content height and the
// board stops filling the wall screen. AppShell supplied that constraint; this
// supplies the same one without the chrome.
const BareShell = ({ children }) => (
  <div className="h-screen w-full bg-background overflow-hidden">{children}</div>
);

function Entry() { const { user } = usePOS(); return <Navigate to={user?.landing || "/login"} replace/>; }


function App() {
  // Global toast dismiss: tap or swipe (left / right / up) anywhere on a toast
  // to dismiss it. Sonner only supports upward swipe natively; this adds the
  // rest via event delegation — zero per-toast listeners, pure CSS animation.
  React.useEffect(() => {
    const SWIPE_THRESHOLD = 50;
    let startX = 0, startY = 0, target = null;

    const findToast = (el) => {
      while (el && el !== document.body) {
        if (el.hasAttribute?.("data-sonner-toast")) return el;
        el = el.parentElement;
      }
      return null;
    };

    const dismiss = (toastEl, direction) => {
      const tx = direction === "left" ? "-100%" : direction === "right" ? "100%" : "0";
      const ty = direction === "up" ? "-100%" : "0";
      toastEl.style.transition = "transform .2s ease, opacity .2s ease";
      toastEl.style.transform = `translate(${tx}, ${ty})`;
      toastEl.style.opacity = "0";
      setTimeout(() => {
        const id = toastEl.getAttribute("data-sonner-toast");
        if (id) toast.dismiss(id);
      }, 200);
    };

    const onTouchStart = (e) => {
      target = findToast(e.target);
      if (!target) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      if (!target) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      const el = target;
      target = null;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        dismiss(el, dx < 0 ? "left" : "right");
      } else if (dy < -SWIPE_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
        // upward swipe — let sonner's native handler run, but this is fallback
        dismiss(el, "up");
      }
    };
    const onClick = (e) => {
      const el = findToast(e.target);
      if (!el) return;
      const id = el.getAttribute("data-sonner-toast");
      if (id) toast.dismiss(id);
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("click", onClick);
    const removeDatePicker = installDatePickerOnClick(document);
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("click", onClick);
      removeDatePicker();
    };
  }, []);

  return (
    <POSProvider><BrowserRouter><RouteLogger/><RemoteBanner/><InsecureBanner/><Routes>
      <Route path="/" element={<Entry/>}/>
      <Route path="/login" element={<LoginPage/>}/>
      <Route path="/license" element={<LicensePage/>}/>
      <Route path="/billing" element={<Protected page="billing" Shell={AppShell}><Outlet/></Protected>}>
        <Route index element={<PhonePos><BillingTables /></PhonePos>} />
        <Route path="table/:tableId" element={<BillingTerminal />} />
      </Route>
      <Route path="/orders" element={<Protected page="orders"><Outlet/></Protected>}>
        <Route index element={<OrdersPage />} />
        <Route path="history" element={<OrderHistoryPage />} />
      </Route>
      <Route path="/inventory" element={<Protected page="inventory"><InventoryPage/></Protected>}/>
      <Route path="/purchase" element={<Protected page="purchase"><PurchasePage/></Protected>}/>
      <Route path="/approvals" element={<Protected page="approvals"><ApprovalsPage/></Protected>}/>
      <Route path="/approvals/:id" element={<Protected page="approvals"><ApprovalsPage/></Protected>}/>
      <Route path="/register" element={<Protected page="register"><RegisterPage/></Protected>}/>
      <Route path="/crm" element={<Protected page="crm"><CRMKhataPage/></Protected>}/>
      <Route path="/reports" element={<Protected page="reports"><EnterpriseReportsPage/></Protected>}/>
      <Route path="/dashboard" element={<Protected page="dashboard"><DashboardPage/></Protected>}/>
      <Route path="/staff" element={<Protected page="staff"><StaffPage/></Protected>}/>
      <Route path="/staff/attendance" element={<Protected page="attendance"><StaffAttendancePage/></Protected>}/>
      <Route path="/logging" element={<Protected page="logging"><LoggingPage/></Protected>}/>
      {/* The CaptainPAD remake is the ONLY captain route. It captures orders into
          IndexedDB, replicates them peer-to-peer, and reconciles them into real
          `orders` rows through the same command boundary an online order uses
          (bible §39.1) — verified end to end by captain-mesh.spec.js Test 5.

          The previous server-backed CaptainPage is deliberately not mounted. It
          created an order on tap (POST /orders before a single item was punched),
          which left phantom empty orders holding tables — order 730 on 2026-08-16
          was one. A rollback screen that manufactures phantom orders is more
          dangerous than no rollback screen. The component stays in git history;
          /captain/classic falls through to the catch-all and redirects. */}
      {/* The captainpad LicenseGate now lives inside CaptainLayout. Wrapping it
          here did nothing: that Shell renders its own <Outlet> and never
          renders the `children` Protected hands it, so the gate never mounted. */}
      <Route path="/captain" element={<Protected page="captain" Shell={CaptainLayout}><Outlet/></Protected>}>
        <Route index element={<CaptainTables />} />
        <Route path="table/:tableId/customer" element={<CaptainCustomer />} />
        <Route path="table/:tableId/menu" element={<CaptainMenu />} />
        <Route path="table/:tableId/summary" element={<CaptainSummary />} />
        <Route path="table/:tableId/bill" element={<CaptainBill />} />
        <Route path="table/:tableId/kots" element={<CaptainKots />} />
        <Route path="pending-bills" element={<CaptainPendingBills />} />
        <Route path="logging" element={<LoggingPage />} />
        <Route path="bill/:orderId" element={<CaptainBill />} />
      </Route>
      <Route path="/kds" element={<Protected page="kds" Shell={AppShell}><LicenseGate addon="kds"><KDSPage/></LicenseGate></Protected>}/>
      <Route path="/admin" element={<Protected page="admin"><AdminHubPage/></Protected>}/>
      <Route path="*" element={<Entry/>}/>
    </Routes><Toaster richColors position="top-center" duration={4000} /></BrowserRouter></POSProvider>
  );
}

export default App;
